import { Alert, showToast } from '@unmind/design-system';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UpcomingAppointments_upcomingAppointments } from 'Book/shared/hooks/useGetUpcomingEAPAppointments/__generated__/UpcomingAppointments';
import { getUserLocale } from 'i18n/getUserLocale';
import { getTimeZone } from 'utils/dates';
import { talkEventRequestsQuery_unmindEventRequests_edges_node } from 'Services/BeGateway/Talk/__generated__/talkEventRequestsQuery';
import { talkEventsQuery_unmindEvents_edges_node } from 'Services/BeGateway/Talk/__generated__/talkEventsQuery';
import type { getServices_bookableServices } from 'Book/pages/Book/hooks';

// We import sibling shared modules by relative path, not the `Book/shared`
// barrel: this component is re-exported from that barrel, so going through
// it would be a cycle.
import { appointmentModeMap } from '../../utils/appointmentModeMap';
import { getMeetURL } from '../../utils/getMeetURL';
import { getSessionPrep } from '../../utils/getSessionPrep';
import { SessionDetails } from '../SessionDetails';
import { SessionDetailsIcon } from '../SessionDetailsIcon';
import { Section } from './styles';

export interface IUpcomingAppointment {
  bookableServices?: getServices_bookableServices[];
  upcomingEapAppointments?: UpcomingAppointments_upcomingAppointments[];
  upcomingTherapyAppointments?: (
    | talkEventsQuery_unmindEvents_edges_node
    | talkEventRequestsQuery_unmindEventRequests_edges_node
  )[];
  userID?: string;
  /**
   * Rendered in place of the appointment list when there is nothing to show —
   * including after the last appointment is cancelled, which is tracked in
   * local state here rather than by the caller. Each page supplies its own
   * empty state (e.g. a practitioner list) so this component stays agnostic
   * about where it is mounted.
   */
  fallback?: React.ReactNode;
}

export default ({
  bookableServices = [],
  upcomingEapAppointments = [],
  upcomingTherapyAppointments = [],
  userID,
  fallback = null,
}: IUpcomingAppointment) => {
  const { t } = useTranslation('book');
  const locale = getUserLocale();
  const browserTimeZone = getTimeZone();

  const [eapAppointments, setEapAppointments] = useState(
    upcomingEapAppointments,
  );

  useEffect(() => {
    if (upcomingEapAppointments) {
      setEapAppointments(upcomingEapAppointments);
    }
  }, [upcomingEapAppointments]);

  if (
    (!eapAppointments?.length || !bookableServices.length) &&
    !upcomingTherapyAppointments.length
  ) {
    return <>{fallback}</>;
  }

  return (
    <Section data-testid="upcoming-appointments">
      <h2>{t('book.upcomingAppointments')}</h2>
      <ul className="appointment-cards">
        {eapAppointments.map(appointment => {
          const service = bookableServices.find(
            s => s.id === appointment.service.id,
          );
          const mode = service?.appointmentTypes.find(
            a => a.mode === appointment.mode,
          );

          const endDateTime = new Date(appointment.dateFrom);
          endDateTime.setUTCMinutes(
            endDateTime.getUTCMinutes() + (mode?.durationMinutes || 30),
          );

          const counsellorName = t(
            'service.form.confirmDetails.counsellorName.fallback.startSentence',
          );

          return (
            <li key={appointment.id} className="eap-service">
              <SessionDetails
                appointment={{
                  id: appointment.id,
                  type: appointment.mode,
                  timeZone: appointment.timeZone,
                  onCancel: () => {
                    const newAppointments = eapAppointments.filter(
                      a => a.id !== appointment.id,
                    );
                    setEapAppointments(newAppointments);
                  },
                }}
                counsellor={{ name: appointment.counsellorName }}
                dates={{
                  startDate: appointment.dateFrom,
                  startTime: new Date(appointment.dateFrom).toLocaleTimeString(
                    locale,
                    {
                      hour: '2-digit',
                      minute: '2-digit',
                      hourCycle: 'h23',
                      timeZone: 'UTC',
                    },
                  ),
                  endDateTime,
                }}
                type={appointment.service as getServices_bookableServices}
                showAddToCalendar
                canCancel={{ button: 'top' }}
                trailingContent={
                  <>
                    <SessionDetailsIcon
                      service={appointment.service?.serviceSlug}
                    />
                    <Alert
                      variant="info"
                      style="no-border"
                      size="small"
                      title={t(
                        `appointment_type.${
                          appointmentModeMap[appointment.mode]
                        }_instructions`,
                        {
                          counsellorName,
                        },
                      )}
                      className="alert"
                    />
                  </>
                }
              />
            </li>
          );
        })}

        {upcomingTherapyAppointments.map((appointment, i) => {
          const isRequest = (
            item:
              | talkEventsQuery_unmindEvents_edges_node
              | talkEventRequestsQuery_unmindEventRequests_edges_node,
          ): item is talkEventRequestsQuery_unmindEventRequests_edges_node =>
            item.__typename === 'EventRequest';

          const practitioner = isRequest(appointment)
            ? appointment.preferredPractitioner
            : appointment.userProfile;

          return (
            <li key={appointment.id} className="therapy-and-coaching">
              <SessionDetails
                appointment={{
                  id: appointment.id,
                  url: isRequest(appointment)
                    ? undefined
                    : getMeetURL(appointment),
                  session: isRequest(appointment)
                    ? undefined
                    : {
                        externalRef: appointment.session?.externalRef,
                        password: appointment.session?.password || undefined,
                        sessionPrep: getSessionPrep(appointment, userID, i),
                      },
                  platform:
                    'sessionType' in appointment
                      ? appointment.sessionType || undefined
                      : undefined,
                  timeZone: isRequest(appointment)
                    ? undefined
                    : browserTimeZone,
                  onCancel: () => {
                    showToast(
                      t(
                        `sessionDetails.cancel.${
                          isRequest(appointment) ? 'request' : 'appointment'
                        }.toast`,
                      ),
                    );
                  },
                }}
                counsellor={{
                  name: `${practitioner?.firstName} ${practitioner?.lastName}`,
                  firstName: practitioner?.firstName,
                  picture: practitioner?.photoUrls[0]?.url,
                }}
                dates={
                  isRequest(appointment)
                    ? undefined
                    : {
                        startDate: new Date(appointment.beginsAt),
                        startTime: new Date(
                          appointment.beginsAt,
                        ).toLocaleTimeString(locale, {
                          hour: '2-digit',
                          minute: '2-digit',
                          hourCycle: 'h23',
                          timeZone: 'UTC',
                        }),
                        endDateTime: new Date(appointment.endsAt),
                        timezone: browserTimeZone,
                      }
                }
                type={isRequest(appointment) ? 'request' : 'appointment'}
                showAddToCalendar
                canCancel={{ button: 'top' }}
              />
            </li>
          );
        })}
      </ul>
    </Section>
  );
};
